import type { LessonConfig } from '../../engine/plant/types';

/** Lessons 6 and 7: cascade, and ratio with feed-forward. */
export const lessons: LessonConfig[] = [
  {
    id: 'L6',
    number: 6,
    title: 'Cascade control',
    summary: 'Two controllers in series, and why that beats one every time.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'intro',
        title: 'The problem cascade solves',
        focusLoop: 'TIC-201',
        body:
          'TIC-201 holds the reactor at 350 K by adjusting the cooling-water flow. But it does ' +
          'not adjust the *valve* — it adjusts the **setpoint of FIC-202**, a second controller ' +
          'whose only job is to make the cooling-water flow follow that setpoint.\n\n' +
          'Why the extra layer? Because the valve is not the flow. Header pressure moves, the ' +
          'valve sticks, the characteristic is non-linear. Left to itself, a temperature ' +
          'controller would have to sort all of that out through a process with a four-minute ' +
          'time constant.',
      },
      {
        id: 'look',
        title: 'Look at both faceplates',
        focusLoop: 'FIC-202',
        body:
          'Open TIC-201 and FIC-202 side by side.\n\n' +
          'TIC-201 is in **AUTO**; its output is a percentage. FIC-202 is in **CAS**; its ' +
          'setpoint is greyed out, because it belongs to TIC-201 now. The primary’s output, ' +
          'scaled across the secondary’s range, *is* the secondary’s setpoint.\n\n' +
          'The inner loop is fast — a few seconds. The outer loop is slow — minutes. That ' +
          'separation is what makes a cascade work: at least three to five times faster inside ' +
          'than outside, or the two fight each other.',
      },
      {
        id: 'break',
        title: 'Break the cascade',
        focusLoop: 'FIC-202',
        check: { op: 'mode', loop: 'FIC-202', is: 'AUTO' },
        body:
          'Put **FIC-202 into AUTO**.\n\n' +
          'You have just taken the secondary out of cascade. Look at TIC-201: it now says ' +
          '**Tracking**. The primary is no longer in charge, so rather than letting its output ' +
          'drift off to some meaningless number, it follows the secondary’s actual setpoint.',
      },
      {
        id: 'drive',
        title: 'Drive the secondary yourself',
        focusLoop: 'FIC-202',
        check: { op: '>', tag: 'FIC-202.SP', value: 20 },
        body:
          'Set FIC-202’s setpoint to **22 m³/h**.\n\n' +
          'Watch TIC-201’s output follow it. The reactor will cool below setpoint, and the ' +
          'primary cannot do anything about it — that is what "out of cascade" means.',
      },
      {
        id: 'restore',
        title: 'Put it back',
        focusLoop: 'FIC-202',
        check: { op: 'mode', loop: 'FIC-202', is: 'CAS' },
        body:
          'Put **FIC-202 back into CAS**.\n\n' +
          'The setpoint did not jump. Because the primary had been tracking, its output was ' +
          'already sitting exactly where the secondary’s setpoint was. That is bumpless transfer ' +
          'in the other direction, and it is the reason tracking exists.',
      },
      {
        id: 'test',
        title: 'Now see what it buys you',
        focusLoop: 'TIC-201',
        body:
          'Open the Sandbox screen and inject **Jacket valve stiction**.\n\n' +
          'With the cascade intact, FIC-202 fights the sticking valve directly and the reactor ' +
          'temperature barely notices. Take FIC-202 out of CAS and the same stiction lands on ' +
          'the temperature loop, which is a hundred times slower and cannot correct it.\n\n' +
          'That is the whole argument for cascade: put a fast loop around the thing that misbehaves, ' +
          'so the slow loop never has to see it.',
      },
    ],
  },

  {
    id: 'L7',
    number: 7,
    title: 'Ratio and feed-forward',
    summary: 'Acting on a disturbance you can measure, instead of waiting for its effect.',
    scenarioId: 'steady',
    steps: [
      {
        id: 'ratio',
        title: 'Ratio control',
        focusLoop: 'FFIC-102',
        body:
          'Open **FFIC-102**, the feed B controller.\n\n' +
          'Its setpoint is not a number you chose. It is **0.50 × the measured feed A flow**. ' +
          'Feed A is the "wild" stream — set by the unit rate — and feed B follows it in a fixed ' +
          'proportion.\n\n' +
          'You cannot type a setpoint into a ratio loop. You type a *ratio*, in the R box.',
      },
      {
        id: 'ratio-do',
        title: 'Move the wild flow',
        focusLoop: 'FIC-101',
        check: { op: '<', tag: 'FIC-101.SP', value: 6.5 },
        body:
          'Set **FIC-101** to **6 m³/h**.\n\n' +
          'Feed B follows immediately to 3 m³/h. It did not wait for a composition analyser to ' +
          'notice the mixture had changed; it acted on the flow measurement, which is instant.\n\n' +
          'That is the point of ratio control: the correct action is known in advance, so there ' +
          'is no reason to wait for an error.',
      },
      {
        id: 'ratio-change',
        title: 'Change the ratio',
        focusLoop: 'FFIC-102',
        body:
          'Set the ratio to **0.8** and watch the reactor.\n\n' +
          'More solvent at the same reactant flow means a more dilute reactor: less heat release, ' +
          'lower conversion, and a jacket with more margin. It is a legitimate operating lever ' +
          'when the cooling is tight — and this unit has scenarios where you will need it.\n\n' +
          'Put it back to **0.5** when you have seen it.',
      },
      {
        id: 'ff-intro',
        title: 'Feed-forward',
        focusLoop: 'FFIC-102',
        body:
          'Feed-forward is the same idea applied to a disturbance rather than a setpoint.\n\n' +
          'Look at the water treatment plant’s coagulant controller if you want the clearest ' +
          'example: the alum dose is computed from the raw water flow *and* the raw turbidity, ' +
          'because the correct dose is known from theory. The controller only trims that ' +
          'calculation.\n\n' +
          '  **OP = feed-forward(measured disturbance) + feedback trim**',
      },
      {
        id: 'ff-why',
        title: 'Why bother?',
        focusLoop: 'FFIC-102',
        body:
          'Because feedback is, by definition, too late. A feedback controller cannot act until ' +
          'the disturbance has already moved the process variable — and if the process has ' +
          'twenty minutes of dead time, that means twenty minutes of off-spec product.\n\n' +
          'Feed-forward acts at the instant the disturbance is measured. Feedback then cleans up ' +
          'whatever the calculation got wrong.\n\n' +
          'The catch: feed-forward is only as good as the model behind it, and a wrong ' +
          'feed-forward makes things worse in a way that a feedback controller cannot always fix. ' +
          'That is why its authority is usually limited — the coagulant loop’s trim is capped at ' +
          '2.5 per cent of pump capacity, for exactly that reason.',
      },
    ],
  },
];
